import { lazy, Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { Skeleton } from '@/components/ui/skeleton';
import { BatchProgressFloat } from '@/components/admin/BatchProgressFloat';
import { ScpLogo } from '@/components/ScpLogo';

// 즉시 로드: 첫 진입 페이지
import { HomePage } from '@/pages/HomePage';
import { LoginPage } from '@/pages/LoginPage';
import { SignUpPage } from '@/pages/SignUpPage';

// Lazy 로드: 일반 페이지
const NovelsPage = lazy(() => import('@/pages/NovelsPage').then(m => ({ default: m.NovelsPage })));
const NovelDetailPage = lazy(() => import('@/pages/NovelDetailPage').then(m => ({ default: m.NovelDetailPage })));
const ChapterReaderPage = lazy(() => import('@/pages/ChapterReaderPage').then(m => ({ default: m.ChapterReaderPage })));
const RankingPage = lazy(() => import('@/pages/RankingPage').then(m => ({ default: m.RankingPage })));
const PaymentPage = lazy(() => import('@/pages/PaymentPage').then(m => ({ default: m.PaymentPage })));
const PaymentSuccessPage = lazy(() => import('@/pages/PaymentSuccessPage').then(m => ({ default: m.PaymentSuccessPage })));
const CardBattlePage = lazy(() => import('@/pages/CardBattlePage').then(m => ({ default: m.CardBattlePage })));
const ArcadePage = lazy(() => import('@/pages/ArcadePage').then(m => ({ default: m.ArcadePage })));
const BattleReplayPage = lazy(() => import('@/pages/BattleReplayPage').then(m => ({ default: m.BattleReplayPage })));
const BattleArenaPage = lazy(() => import('@/pages/BattleArenaPage').then(m => ({ default: m.BattleArenaPage })));
const ScpListPage = lazy(() => import('@/pages/ScpListPage').then(m => ({ default: m.ScpListPage })));
const ScpDetailPage = lazy(() => import('@/pages/ScpDetailPage').then(m => ({ default: m.ScpDetailPage })));
const FriendsPage = lazy(() => import('@/pages/FriendsPage').then(m => ({ default: m.FriendsPage })));
const UserProfilePage = lazy(() => import('@/pages/UserProfilePage').then(m => ({ default: m.UserProfilePage })));
const AuthorDashboardPage = lazy(() => import('@/pages/AuthorDashboardPage').then(m => ({ default: m.AuthorDashboardPage })));
const AuthorNovelPage = lazy(() => import('@/pages/AuthorNovelPage').then(m => ({ default: m.AuthorNovelPage })));
const AspirantPage = lazy(() => import('@/pages/AspirantPage').then(m => ({ default: m.AspirantPage })));
const TermsPage = lazy(() => import('@/pages/TermsPage'));
const PrivacyPage = lazy(() => import('@/pages/PrivacyPage'));
const RefundPolicyPage = lazy(() => import('@/pages/RefundPolicyPage'));
const ContactPage = lazy(() => import('@/pages/ContactPage'));
const AccountPage = lazy(() => import('@/pages/AccountPage'));
const NoticesPage = lazy(() => import('@/pages/NoticesPage').then(m => ({ default: m.NoticesPage })));
const NoticeDetailPage = lazy(() => import('@/pages/NoticesPage').then(m => ({ default: m.NoticeDetailPage })));

// Lazy 로드: 관리자 페이지
const AdminDashboard = lazy(() => import('@/pages/admin/DashboardPage').then(m => ({ default: m.DashboardPage })));
const AdminUsers = lazy(() => import('@/pages/admin/UsersPage').then(m => ({ default: m.UsersPage })));
const AdminNovels = lazy(() => import('@/pages/admin/NovelsManagePage').then(m => ({ default: m.NovelsManagePage })));
const AdminComments = lazy(() => import('@/pages/admin/CommentsManagePage').then(m => ({ default: m.CommentsManagePage })));
const AdminScp = lazy(() => import('@/pages/admin/ScpManagePage').then(m => ({ default: m.ScpManagePage })));
const AdminScpDetail = lazy(() => import('@/pages/admin/ScpDetailEditPage').then(m => ({ default: m.ScpDetailEditPage })));
const AdminCards = lazy(() => import('@/pages/admin/CardsManagePage').then(m => ({ default: m.CardsManagePage })));
const AdminMissions = lazy(() => import('@/pages/admin/MissionsManagePage').then(m => ({ default: m.MissionsManagePage })));
const AdminSettings = lazy(() => import('@/pages/admin/SettingsPage').then(m => ({ default: m.SettingsPage })));
const AdminNotices = lazy(() => import('@/pages/admin/NoticesManagePage').then(m => ({ default: m.NoticesManagePage })));
const AdminAnalytics = lazy(() => import('@/pages/admin/AnalyticsPage').then(m => ({ default: m.AnalyticsPage })));
const AdminPayments = lazy(() => import('@/pages/admin/PaymentsPage').then(m => ({ default: m.PaymentsPage })));
const AdminPointsAudit = lazy(() => import('@/pages/admin/PointsAuditPage').then(m => ({ default: m.PointsAuditPage })));
const AdminPointsEconomy = lazy(() => import('@/pages/admin/PointsEconomyPage').then(m => ({ default: m.PointsEconomyPage })));
const AdminApiKeys = lazy(() => import('@/pages/admin/ApiKeysPage').then(m => ({ default: m.ApiKeysPage })));
const AdminNovelDetail = lazy(() => import('@/pages/admin/NovelDetailManagePage').then(m => ({ default: m.NovelDetailManagePage })));
const AdminBots = lazy(() => import('@/pages/admin/BotsManagePage').then(m => ({ default: m.BotsManagePage })));

function PageFallback() {
  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4">
      <ScpLogo size={48} className="animate-pulse" />
      <div className="space-y-2 w-full max-w-md">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-2/3" />
      </div>
    </div>
  );
}

function App() {
  return (
    <>
    <BatchProgressFloat />
    <ErrorBoundary>
    <Suspense fallback={<PageFallback />}>
      <Routes>
        {/* 관리자 페이지 (AdminLayout 사용, 일반 Layout 없음) */}
        <Route path="/admin" element={<ProtectedRoute requireRole={['admin', 'super-admin']}><AdminDashboard /></ProtectedRoute>} />
        <Route path="/admin/users" element={<ProtectedRoute requireRole={['admin', 'super-admin']}><AdminUsers /></ProtectedRoute>} />
        <Route path="/admin/novels" element={<ProtectedRoute requireRole={['admin', 'super-admin']}><AdminNovels /></ProtectedRoute>} />
        <Route path="/admin/novels/:novelId" element={<ProtectedRoute requireRole={['admin', 'super-admin']}><AdminNovelDetail /></ProtectedRoute>} />
        <Route path="/admin/comments" element={<ProtectedRoute requireRole={['admin', 'super-admin']}><AdminComments /></ProtectedRoute>} />
        <Route path="/admin/scp" element={<ProtectedRoute requireRole={['admin', 'super-admin']}><AdminScp /></ProtectedRoute>} />
        <Route path="/admin/scp/:scpCode" element={<ProtectedRoute requireRole={['admin', 'super-admin']}><AdminScpDetail /></ProtectedRoute>} />
        <Route path="/admin/cards" element={<ProtectedRoute requireRole={['admin', 'super-admin']}><AdminCards /></ProtectedRoute>} />
        <Route path="/admin/missions" element={<ProtectedRoute requireRole={['admin', 'super-admin']}><AdminMissions /></ProtectedRoute>} />
        <Route path="/admin/settings" element={<ProtectedRoute requireRole={['admin', 'super-admin']}><AdminSettings /></ProtectedRoute>} />
        <Route path="/admin/payments" element={<ProtectedRoute requireRole={['admin', 'super-admin']}><AdminPayments /></ProtectedRoute>} />
        <Route path="/admin/notices" element={<ProtectedRoute requireRole={['admin', 'super-admin']}><AdminNotices /></ProtectedRoute>} />
        <Route path="/admin/analytics" element={<ProtectedRoute requireRole={['admin', 'super-admin']}><AdminAnalytics /></ProtectedRoute>} />
        <Route path="/admin/points" element={<ProtectedRoute requireRole={['admin', 'super-admin']}><AdminPointsEconomy /></ProtectedRoute>} />
        <Route path="/admin/points/audit" element={<ProtectedRoute requireRole={['admin', 'super-admin']}><AdminPointsAudit /></ProtectedRoute>} />
        <Route path="/admin/api-keys" element={<ProtectedRoute requireRole={['admin', 'super-admin']}><AdminApiKeys /></ProtectedRoute>} />
        <Route path="/admin/bots" element={<ProtectedRoute requireRole={['admin', 'super-admin']}><AdminBots /></ProtectedRoute>} />

        {/* 전체화면 페이지 (Layout 없음) */}
        <Route path="/arcade/battle/arena" element={<BattleArenaPage />} />

        {/* 일반 페이지 (Header + Footer Layout) */}
        <Route path="/*" element={
          <Layout>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignUpPage />} />
              <Route path="/novels" element={<NovelsPage />} />
              <Route path="/novels/:id" element={<NovelDetailPage />} />
              <Route path="/novels/:novelId/chapters/:chapterId" element={<ChapterReaderPage />} />
              <Route path="/scp" element={<ScpListPage />} />
              <Route path="/scp/:scpNumber" element={<ScpDetailPage />} />
              <Route path="/ranking" element={<RankingPage />} />
              <Route path="/payment" element={<ProtectedRoute><PaymentPage /></ProtectedRoute>} />
              <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccessPage /></ProtectedRoute>} />
              <Route path="/arcade" element={<ProtectedRoute><ArcadePage /></ProtectedRoute>} />
              <Route path="/arcade/battle" element={<CardBattlePage />} />
              <Route path="/arcade/battle/:battleId" element={<BattleReplayPage />} />
              <Route path="/friends" element={<ProtectedRoute><FriendsPage /></ProtectedRoute>} />
              <Route path="/users/:userId" element={<UserProfilePage />} />
              <Route path="/author" element={<ProtectedRoute requireRole={['aspirant', 'author', 'admin', 'super-admin']}><AuthorDashboardPage /></ProtectedRoute>} />
              <Route path="/author/novels/:novelId" element={<ProtectedRoute requireRole={['aspirant', 'author', 'admin', 'super-admin']}><AuthorNovelPage /></ProtectedRoute>} />
              <Route path="/aspirant" element={<AspirantPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/refund-policy" element={<RefundPolicyPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/account" element={<ProtectedRoute><AccountPage /></ProtectedRoute>} />
              <Route path="/notices" element={<NoticesPage />} />
              <Route path="/notices/:id" element={<NoticeDetailPage />} />
            </Routes>
          </Layout>
        } />
      </Routes>
    </Suspense>
    </ErrorBoundary>
    </>
  );
}

export default App;
